import { Component, h } from 'preact';
import linkState from 'linkstate';
import { route } from 'preact-router';

import { HopplerModalizer } from '../../common/hoppler-modalizer.component';
import { FileUploadFormControl } from '../../common/file-upload-form-control.component';
import { ReqState } from '../../../../../types/request-state.model';
import { InquiryDocument } from '../../../../../types/inquiries/inquiry-document.model';
import { OFFER_SENT_STATUSES } from '../../../../../constants/inquiries';
import { DatePickerControl } from '../../common/date-picker-control.component';
import { InqStageTransitionContent } from './inq-stage-transition-content.component';

interface InqUploadOfferModalProps {
  documents: InquiryDocument[];
  offersReqState: ReqState;
  submitState: ReqState;

  onOfferSentSubmit: (loiKey: string, message: string) => void;
  onOfferAcceptedSubmit: (loiKey: string, date: string, files: FileList) => void;
  onClose: () => void;
}

interface InqUploadOfferModalState {
  loiKey?: string;
  progressChoice?: OfferProgressChoice;

  message?: string;

  acceptedDate?: string;
  files?: FileList;
}

enum OfferProgressChoice {
  OFFER_SENT = 'Offer Sent',
  OFFER_ACCEPTED = 'Offer Accepted'
};
const OFFER_PROGRESS_CHOICES = [
  OfferProgressChoice.OFFER_SENT,
  OfferProgressChoice.OFFER_ACCEPTED
];

export class InqUploadOfferModal extends Component<InqUploadOfferModalProps, InqUploadOfferModalState> {
  isSubmitButtonEnabled() {
    const state = this.state;

    if (!state.loiKey) {
      return false;
    }

    if (state.progressChoice === OfferProgressChoice.OFFER_SENT) {
      return !!state.message;
    } else {
      return !!state.acceptedDate;
    }
  }

  onSubmit(event: Event) {
    event.preventDefault();

    const state = this.state;
    const progressChoice = state.progressChoice;

    if (progressChoice === OfferProgressChoice.OFFER_SENT) {
      this.props.onOfferSentSubmit(state.loiKey, state.message);
    } else {
      this.props.onOfferAcceptedSubmit(state.loiKey, state.acceptedDate, state.files);
    }
  }

  onProceed(stageSlug) {
    route(`/kumita-brokers/inquiries/${ stageSlug }?actionFrom=upload-offer-modal`);
    this.props.onClose();
  }

  //////////////////////////////////////////////////////////////////////////////
  // UI
  //////////////////////////////////////////////////////////////////////////////
  getOfferSelectBox() {
    const { documents, offersReqState: loiReqState } = this.props;

    if (loiReqState === ReqState.SUCCESS) {
      if (documents.length > 0) {
        return (
          <select className="form-control" value={ this.state.loiKey } onChange={ linkState(this, 'loiKey') }>
            { documents.map(doc => (
              <option value={ doc.loiKey }>{ doc.documentName || doc.propertyAddress }</option>
            ))}
          </select>
        );
      } else { 
        return (
          <p className="mb-0">
            <strong className="text-danger d-block mb-2">This inquiry has no existing offers.</strong>
            Please create an offer before marking an offer as sent.
          </p>
        );
      }
    } else {
      return (
        <input type="text" className="form-control" disabled placeholder="Loading offers for this inquiry..." />
      );
    }
  }

  getSentStatusField(props: InqUploadOfferModalProps, state: InqUploadOfferModalState) {
    if (state.progressChoice === OfferProgressChoice.OFFER_SENT) {
      return (
        <div className="hi-input-group">
          <label className="hi-input-group__label">Status</label>
          <select className="form-control" onChange={ linkState(this, 'message') } value={ state.message || 'Select' }>
            <option disabled>Select</option>
            { OFFER_SENT_STATUSES.map(status => (
              <option value={ status }>{ status }</option>
            ))}
          </select>
        </div>
      );
    }
  }

  getAcceptedDateField(props: InqUploadOfferModalProps, state: InqUploadOfferModalState) {
    if (state.progressChoice === OfferProgressChoice.OFFER_ACCEPTED) {
      return (
        <div className="hi-input-group">
          <label className="hi-input-group__label">Accepted Date</label>
          <DatePickerControl className="form-control"
            onChange={ linkState(this, 'acceptedDate') }
            value={ state.acceptedDate } />
        </div>
      );
    }
  }

  getFileField(props: InqUploadOfferModalProps, state: InqUploadOfferModalState) {
    if (state.progressChoice === OfferProgressChoice.OFFER_ACCEPTED) {
      return (
        <div className="hi-input-group">
          <label className="hi-input-group__label">Upload File</label>
          <FileUploadFormControl selectedFiles={ this.state.files }
            onChange={ linkState(this, 'files', 'target.files') } />
        </div>
      );
    }
  }

  getSubmitButton(props: InqUploadOfferModalProps, state: InqUploadOfferModalState) {
    let text = 'Upload';

    if (state.progressChoice === OfferProgressChoice.OFFER_SENT) {
      text = 'Mark as Sent';
    }

    if (props.submitState === ReqState.IN_PROGRESS) {
      return (
        <button type="submit" className="btn btn-secondary" disabled>
          <i className="fas fa-cog fa-spin" /> Submitting...
        </button>
      );
    } else {
      return (
        <button type="submit" className="btn btn-secondary" disabled={ !this.isSubmitButtonEnabled() }>
          { text }
        </button>
      );
    }
  }

  getSuccessContent(props: InqUploadOfferModalProps, state: InqUploadOfferModalState) {
    const choice = state.progressChoice;
    const targetStageName = choice === OfferProgressChoice.OFFER_SENT ? 'Offer Sent' : 'Offer Accepted'
    const stageSlug = choice === OfferProgressChoice.OFFER_SENT ? 'offer-sent' : 'offer-accepted';

    return (
      <InqStageTransitionContent onClose={ props.onClose }
        targetName={ targetStageName }
        onProceed={ () => this.onProceed(stageSlug) } />
    );
  }

  getForm(props: InqUploadOfferModalProps, state: InqUploadOfferModalState) {
    return (
      <form onSubmit={ (event) => this.onSubmit(event) } className="hi-inqupoffmod__content">
        <div className="hi-input-group">
          <label className="hi-input-group__label">Select an Offer</label>
          { this.getOfferSelectBox() }
        </div>

        <div className="hi-input-group">
          <label className="hi-input-group__label">Offer Type</label>
          <select className="form-control" value={ state.progressChoice || 'Select' } onChange={ linkState(this, 'progressChoice') }>
            <option disabled>Select</option>
            { OFFER_PROGRESS_CHOICES.map(choice => (
              <option value={ choice }>{ choice }</option>
            )) }
          </select>
        </div>

        {/* Offer Sent fields */}
        { this.getSentStatusField(props, state) }

        {/* Offer Accepted fields */}
        { this.getAcceptedDateField(props, state) }
        { this.getFileField(props, state) }

        <div className="text-center">
          { this.getSubmitButton(props, state) }
        </div>
      </form>
    )
  }

  render(props: InqUploadOfferModalProps, state: InqUploadOfferModalState) {
    return (
      <HopplerModalizer onClose={ () => null }>
        <div className="hi-inqupoffmod">
          <div className="hi-inqupoffmod__title-bar">
            <span className="hi-inqupoffmod__title-label">Upload Offer</span>
            <button className="btn btn-link hi-inqupoffmod__title-close" onClick={ props.onClose }>
              <i className="fas fa-times" /> Close
            </button>
          </div>

          <div className="hi-inqupoffmod__body">
            { props.submitState !== ReqState.SUCCESS ? this.getForm(props, state) : this.getSuccessContent(props, state) }
          </div>
        </div>
      </HopplerModalizer>
    );
  }

  componentWillReceiveProps(nextProps: InqUploadOfferModalProps) {
    if (nextProps.documents && !this.props.documents && nextProps.documents.length > 0) {
      const latestDoc = nextProps.documents[nextProps.documents.length - 1];
      this.setState({ loiKey: latestDoc.loiKey });
    }
  }
}